import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import Reveal from "@/components/Reveal";
import { GlobeHemisphereWest, HeartBreak, Compass, AirplaneTilt, HouseLine } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

const EXPAT_IMG =
  "https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=80&w=1600&auto=format&fit=crop";

const baseChallenges = [
  { icon: GlobeHemisphereWest, text: "Culture shock and the fatigue of constant adaptation" },
  { icon: HeartBreak, text: "Loneliness and the distance from family and lifelong friends" },
  { icon: Compass, text: "Losing your bearings — identity in transition between two worlds" },
  { icon: AirplaneTilt, text: "Expat and third-culture children navigating their own journey" },
  { icon: HouseLine, text: "The invisible mental load of rebuilding a home, again" },
];

export default function ExpatLife() {
  const lenis = useLenis();
  const { t } = useLang();
  const challenges = baseChallenges.map((c, i) => ({ ...c, text: t.expat.challenges[i] }));
  const go = (href) => (e) => {
    e.preventDefault();
    if (lenis) lenis.scrollTo(href, { offset: -70, duration: 1.4 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="expat-life" className="overflow-hidden bg-sage py-24 lg:py-36" data-testid="expat-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="mb-6 text-xs font-semibold uppercase tracking-[0.3em] text-moss">{t.expat.eyebrow}</p>
              <h2 className="font-serif text-4xl leading-tight tracking-tight md:text-5xl lg:text-6xl">
                {t.expat.h2a}<em className="italic font-light text-moss">{t.expat.h2b}</em>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-moss md:text-lg">
                {t.expat.introPre}
                <strong className="font-semibold text-forest">{t.expat.introStrong}</strong>
                {t.expat.introPost}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 space-y-1">
                {challenges.map((c, i) => (
                  <motion.div
                    key={c.text}
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="group flex items-center gap-5 border-b border-forest/10 py-4 transition-all duration-300 hover:pl-3"
                    data-testid={`expat-challenge-${i}`}
                  >
                    <c.icon size={24} weight="duotone" className="shrink-0 text-moss transition-transform duration-300 group-hover:scale-110" />
                    <p className="text-sm leading-relaxed text-forest md:text-base">{c.text}</p>
                  </motion.div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <p className="mt-10 max-w-lg font-serif text-xl italic leading-relaxed text-moss md:text-2xl">
                {t.expat.quote}
              </p>
              <a
                href="#contact"
                onClick={go("#contact")}
                data-testid="expat-contact-btn"
                className="mt-8 inline-block rounded-full bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-moss hover:scale-[1.03] active:scale-95"
              >
                {t.expat.cta}
              </a>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.2}>
            <div className="relative">
              <motion.div
                whileHover={{ scale: 0.985 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden rounded-t-full border border-forest/10 shadow-[0_30px_60px_-20px_rgba(44,53,40,0.25)]"
              >
                <img
                  src={EXPAT_IMG}
                  alt="Traveller with a map, a new life abroad ahead"
                  className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105"
                  data-testid="expat-image"
                />
              </motion.div>
              <div className="absolute -bottom-6 -left-4 rounded-full border border-forest/10 bg-peach px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-forest shadow-[0_20px_40px_-15px_rgba(44,53,40,0.15)] md:-left-8">
                {t.expat.badge}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
